/**
 * Composant d'historique d'une feuille d'heures.
 *
 * Affiche une timeline chronologique de toutes les modifications loguées
 * dans `payroll_corrections` pour une feuille donnée. Réutilisable dans :
 * - AdminValidate (panneau détail)
 * - SecretariatPayroll (modal de correction)
 * - TeamMemberProfile (vue employé)
 */
import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { CheckCircle, XCircle, Edit3, AlertTriangle, FileSignature } from 'lucide-react';

interface AuditEntry {
  id: string;
  weekly_sheet_id: string | null;
  time_entry_id: string | null;
  corrected_by: string;
  actor_role: string | null;
  actor_page: string | null;
  action: string;
  record_type: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  reason: string | null;
  created_at: string;
  corrector?: { full_name: string } | null;
}

interface TimesheetAuditTrailProps {
  sheetId: string;
  compact?: boolean;
}

const ACTION_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  validate: { icon: <CheckCircle size={16} />, color: '#16a34a', label: 'Validation' },
  reject:   { icon: <XCircle size={16} />, color: '#dc2626', label: 'Rejet' },
  correct:  { icon: <AlertTriangle size={16} />, color: '#d97706', label: 'Correction' },
  edit:     { icon: <Edit3 size={16} />, color: '#2563eb', label: 'Modification' },
  submit:   { icon: <FileSignature size={16} />, color: '#7c3aed', label: 'Soumission' },
  create:   { icon: <FileSignature size={16} />, color: '#64748b', label: 'Création' },
  delete:   { icon: <XCircle size={16} />, color: '#94a3b8', label: 'Suppression' },
};

const ROLE_LABELS: Record<string, string> = {
  admin: 'Direction',
  secretariat: 'Secrétariat',
  bureau_etude: 'Bureau d\'étude',
  apprenti_be: 'Apprenti BE',
  chef_chantier: 'Chef de chantier',
  chef_equipe: 'Chef d\'équipe',
  electricien: 'Électricien',
};

const FIELD_LABELS: Record<string, string> = {
  status: 'Statut',
  travel_zone: 'Zone',
  hours_normal: 'Heures normales',
  hours_saturday: 'Heures samedi',
  hours_major: 'Heures dimanche/férié',
  meals_count: 'Panier repas',
  indemnity_transport: 'Transport',
  indemnity_trajet: 'Trajet',
  is_grand_deplacement: 'Grand Déplacement',
  gd_amount: 'Forfait GD',
  gd_travel_hours: 'Heures route GD',
  override_weekly_hours: 'Base horaire',
  overtime_25: 'HS 25%',
  overtime_50: 'HS 50%',
  regular_hours: 'Heures régulières',
  sunday_hours: 'Heures dimanche',
};

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatValue = (val: string | null): string => {
  if (val === null || val === '') return '∅';
  // Booléens
  if (val === 'true') return 'oui';
  if (val === 'false') return 'non';
  return val;
};

export function TimesheetAuditTrail({ sheetId, compact = false }: TimesheetAuditTrailProps) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Fonction de fetch réutilisable (appelée au montage + sur Realtime INSERT)
  const fetchEntries = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    const { data, error } = await supabase
      .from('payroll_corrections')
      .select('*, corrector:employees!corrected_by(full_name)')
      .eq('weekly_sheet_id', sheetId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[TimesheetAuditTrail] fetch error:', error);
      setEntries([]);
    } else {
      setEntries((data ?? []) as AuditEntry[]);
    }
    if (showLoader) setLoading(false);
  }, [sheetId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    // Chargement initial
    fetchEntries(false).finally(() => {
      if (!cancelled) setLoading(false);
    });

    // Realtime : écoute les nouvelles entrées d'audit pour CETTE feuille.
    // Dès qu'une modification est loguée (édition, validation, rejet, correction),
    // on rafraîchit automatiquement la liste sans intervention de l'utilisateur.
    const channel = supabase
      .channel(`audit-trail-${sheetId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'payroll_corrections',
          filter: `weekly_sheet_id=eq.${sheetId}`,
        },
        () => {
          // Refetch complet pour récupérer le join avec employees.full_name
          // (payload.new ne contient pas les relations jointes)
          if (!cancelled) fetchEntries(false);
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [sheetId, fetchEntries]);

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
        Chargement de l'historique...
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem', fontStyle: 'italic' }}>
        Aucune modification enregistrée pour cette feuille.
      </div>
    );
  }

  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      border: '1px solid #e2e8f0',
      padding: compact ? 12 : 16,
      maxHeight: compact ? 320 : 'none',
      overflowY: 'auto',
    }}>
      <h4 style={{
        margin: '0 0 12px',
        fontSize: '0.9rem',
        fontWeight: 700,
        color: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
        <Edit3 size={16} color="#64748b" />
        Historique des modifications ({entries.length})
      </h4>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {entries.map(entry => {
          const cfg = ACTION_CONFIG[entry.action] || ACTION_CONFIG.edit;
          const actorName = entry.corrector?.full_name ?? 'Inconnu';
          const roleLabel = entry.actor_role ? ROLE_LABELS[entry.actor_role] || entry.actor_role : '';
          const fieldLabel = FIELD_LABELS[entry.field_name] || entry.field_name;

          return (
            <div key={entry.id} style={{
              display: 'flex',
              gap: 10,
              padding: 10,
              background: '#f8fafc',
              borderLeft: `3px solid ${cfg.color}`,
              borderRadius: 6,
            }}>
              <div style={{ color: cfg.color, flexShrink: 0, marginTop: 2 }}>
                {cfg.icon}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 8,
                  marginBottom: 2,
                }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#0f172a' }}>
                    {actorName}
                    {roleLabel && (
                      <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 6 }}>
                        ({roleLabel})
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8', flexShrink: 0 }}>
                    {formatDateTime(entry.created_at)}
                  </span>
                </div>

                <div style={{ fontSize: '0.78rem', color: '#475569', lineHeight: 1.5 }}>
                  <span style={{
                    display: 'inline-block',
                    background: cfg.color + '20',
                    color: cfg.color,
                    padding: '1px 6px',
                    borderRadius: 4,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    marginRight: 6,
                  }}>
                    {cfg.label}
                  </span>

                  {entry.action === 'validate' || entry.action === 'reject' ? (
                    <span>de la feuille</span>
                  ) : (
                    <>
                      <strong>{fieldLabel}</strong>
                      {entry.old_value !== null && entry.new_value !== null && (
                        <span style={{ marginLeft: 4, color: '#64748b' }}>
                          : <code style={{ background: '#fee2e2', padding: '0 4px', borderRadius: 3 }}>{formatValue(entry.old_value)}</code>
                          {' → '}
                          <code style={{ background: '#dcfce7', padding: '0 4px', borderRadius: 3 }}>{formatValue(entry.new_value)}</code>
                        </span>
                      )}
                    </>
                  )}
                </div>

                {entry.reason && (
                  <div style={{
                    marginTop: 4,
                    fontSize: '0.75rem',
                    fontStyle: 'italic',
                    color: '#64748b',
                    background: '#fffbeb',
                    padding: '4px 8px',
                    borderRadius: 4,
                  }}>
                    Motif : {entry.reason}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
